import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react';
import { NOTIFICATION_AUTO_CLOSE } from '../config/constants';

/**
 * 統一的錯誤處理工具
 */
export class ErrorHandler {
  /**
   * 顯示錯誤通知
   * @param {string} message - 錯誤訊息
   * @param {string} title - 錯誤標題（可選）
   * @param {Error} error - 錯誤物件（可選，用於記錄詳細資訊）
   */
  static showError(message, title = '操作失敗', error = null) {
    if (error) {
      console.error(`${title}: ${message}`, error);
    } else {
      console.error(`${title}: ${message}`);
    }

    notifications.show({
      title,
      message,
      color: 'red',
      icon: <IconX size={18} />,
      autoClose: NOTIFICATION_AUTO_CLOSE.ERROR,
    });
  }

  /**
   * 顯示成功通知
   * @param {string} message - 成功訊息
   * @param {string} title - 成功標題（可選）
   */
  static showSuccess(message, title = '操作成功') {
    notifications.show({
      title,
      message,
      color: 'green',
      icon: <IconCheck size={18} />,
      autoClose: NOTIFICATION_AUTO_CLOSE.SUCCESS,
    });
  }

  /**
   * 顯示警告通知
   * @param {string} message - 警告訊息
   * @param {string} title - 警告標題（可選）
   */
  static showWarning(message, title = '注意') {
    notifications.show({
      title,
      message,
      color: 'yellow',
      icon: <IconAlertTriangle size={18} />,
      autoClose: NOTIFICATION_AUTO_CLOSE.WARNING,
    });
  }

  /**
   * 處理 Firebase 錯誤
   * @param {Error} error - Firebase 錯誤物件
   * @param {string} operation - 執行的操作名稱
   */
  static handleFirebaseError(error, operation = '操作') {
    let userMessage = '發生未知錯誤，請稍後再試';
    
    // 根據 Firebase 錯誤代碼提供更具體的錯誤訊息
    if (error.code) {
      switch (error.code) {
        case 'permission-denied':
          userMessage = '您沒有執行此操作的權限';
          break;
        case 'unavailable':
          userMessage = '服務暫時無法使用，請稍後再試';
          break;
        case 'deadline-exceeded':
          userMessage = '操作超時，請檢查網路連線';
          break;
        case 'not-found':
          userMessage = '找不到相關資料';
          break;
        case 'already-exists':
          userMessage = '資料已存在';
          break;
        case 'invalid-argument':
          userMessage = '輸入資料格式不正確';
          break;
        case 'resource-exhausted':
          userMessage = '系統資源不足，請稍後再試';
          break;
        case 'unauthenticated':
          userMessage = '請重新登入後再試';
          break;
        default:
          userMessage = error.message || '發生未知錯誤';
      }
    } else if (error.message) {
      userMessage = error.message;
    }

    this.showError(userMessage, `${operation}失敗`, error);
  }

  /**
   * 處理網路錯誤
   * @param {Error} error - 網路錯誤物件
   * @param {string} operation - 執行的操作名稱
   */
  static handleNetworkError(error, operation = '操作') {
    let userMessage = '網路連線異常，請檢查網路設定';
    
    if (!navigator.onLine) {
      userMessage = '目前無網路連線，請檢查網路設定';
    } else if (error.message && error.message.includes('timeout')) {
      userMessage = '網路連線超時，請稍後再試';
    }

    this.showError(userMessage, `${operation}失敗`, error);
  }

  /**
   * 統一的異步操作錯誤處理包裝器
   * @param {Function} asyncOperation - 要執行的異步操作
   * @param {string} operationName - 操作名稱
   * @param {string} successMessage - 成功訊息（可選）
   * @param {Function} onSuccess - 成功回調（可選）
   * @param {Function} onError - 錯誤回調（可選）
   */
  static async handleAsyncOperation(
    asyncOperation, 
    operationName = '操作', 
    successMessage = null,
    onSuccess = null,
    onError = null
  ) {
    try {
      const result = await asyncOperation();
      
      if (successMessage) {
        this.showSuccess(successMessage, `${operationName}成功`);
      }
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (error) {
      // 判斷錯誤類型並處理
      if (error.code && error.code.startsWith('firestore/')) {
        this.handleFirebaseError(error, operationName);
      } else if (error.name === 'NetworkError' || error.message.includes('network')) {
        this.handleNetworkError(error, operationName);
      } else {
        this.showError(error.message || '發生未知錯誤', `${operationName}失敗`, error);
      }
      
      if (onError) {
        onError(error);
      }
      
      throw error; // 重新拋出錯誤，讓調用者可以決定是否需要額外處理
    }
  }

  /**
   * 表單驗證錯誤處理
   * @param {Object} validationErrors - 驗證錯誤物件
   */
  static handleValidationErrors(validationErrors) {
    const errorMessages = Object.values(validationErrors).filter(Boolean);
    if (errorMessages.length > 0) {
      this.showWarning(errorMessages[0], '請檢查輸入資料');
    }
  }
}

// 匯出便利方法
export const showError = ErrorHandler.showError.bind(ErrorHandler);
export const showSuccess = ErrorHandler.showSuccess.bind(ErrorHandler);
export const showWarning = ErrorHandler.showWarning.bind(ErrorHandler);
export const handleFirebaseError = ErrorHandler.handleFirebaseError.bind(ErrorHandler);
export const handleNetworkError = ErrorHandler.handleNetworkError.bind(ErrorHandler);
export const handleAsyncOperation = ErrorHandler.handleAsyncOperation.bind(ErrorHandler);
export const handleValidationErrors = ErrorHandler.handleValidationErrors.bind(ErrorHandler);

export default ErrorHandler;
